import type { NavigationBody } from '@ayumia/contracts';

/**
 * Routage sémantique (59 §4.2, §20.2) : une route désigne une vue, un type d'objet ou un espace de
 * travail, jamais une URL d'implémentation. Le fragment d'URL n'en est que la sérialisation.
 */
export type Route =
  | { kind: 'home' }
  /** `list` : état de la liste sérialisé (ADR-094), forme gouvernée de l'API, revalidé au serveur. */
  | { kind: 'view'; view_ref: string; record_id?: string; list?: string }
  | { kind: 'object'; object_type: string; record_id?: string; list?: string }
  | { kind: 'workspace'; ref: string }
  /** Boîte de travail et tâche (ADR-064 §5) : le travail humain, quel que soit le Pack. */
  | { kind: 'inbox' }
  | { kind: 'task'; work_item_id: string }
  /**
   * Page Tenant (ADR-128) : compte, sociétés et sites, paramètres (ADR-081, l'ancienne page Paramètres) et
   * composition du modèle ; le serveur seul autorise.
   */
  | { kind: 'tenant'; tab: TenantTab }
  /** File d'attention (ADR-082 §9) : projection triée des tâches, constats et écarts bloquants. */
  | { kind: 'attention' }
  /** Sécurité du compte (ADR-091) : second facteur, élévation, mot de passe. */
  | { kind: 'account' }
  /** Thèmes du tenant (ADR-092 §3) : catalogue, édition avec aperçu, choix et retour arrière ; le serveur seul autorise. */
  | { kind: 'themes' }
  /** Médiathèque (spec 192 §13, ADR-106) : images ou documents, à la racine ou dans un dossier. */
  | { kind: 'media'; library: 'images' | 'documents'; folder_id?: string }
  /** Prise de rendez-vous de l'entretien d'implémentation : page publique, sans session (POC §9A). */
  | { kind: 'booking' }
  /** Entretien d'implémentation mené par l'IA : page publique ouverte par le lien du client (POC §10). */
  | { kind: 'interview' }
  /** Back-office AYUMIA des entretiens : liste des rendez-vous et fiche d'un rendez-vous (démonstration locale). */
  | { kind: 'backoffice'; engagement_id?: string }
  /**
   * Explorateur du Business Graph (ADR-129) : sans objet, la recherche d'un objet de départ ; avec, son
   * voisinage ou sa traçabilité. Mode, profondeur, sens et date d'effet sont dans l'adresse, pour partager.
   */
  | ({ kind: 'graph'; object_type?: string; record_id?: string } & GraphOptions)
  /** Atlas du modèle effectif (ADR-130 tranche 1), avec l'objet choisi. */
  | { kind: 'model'; object_type?: string }
  /** Studio › Formulaires (ADR-141 §2, tranche 3) : liste des gabarits, ou l'éditeur d'un gabarit. */
  | { kind: 'forms'; artifact_id?: string }
  /** Studio › Vues (ADR-130 tranche 2b) : liste des vues de liste, ou l'éditeur d'une vue. */
  | { kind: 'views'; artifact_id?: string };

/** Réglages d'une exploration (ADR-129 §2) : voisinage sur la projection, traçabilité sur l'autoritaire. */
export interface GraphOptions {
  mode?: 'neighborhood' | 'trace';
  /** Voisinage : 1 ou 2 sauts ; traçabilité : 1 à 10. */
  depth?: number;
  /** Traçabilité seulement. */
  direction?: 'upstream' | 'downstream';
  /** Date d'effet ISO 8601 (traçabilité seulement). */
  at?: string;
}

/** Onglets de la page Tenant (ADR-128). */
export type TenantTab = 'account' | 'companies' | 'sites' | 'parameters' | 'composition';
const TENANT_TAB_SEGMENTS: Readonly<Record<TenantTab, string>> = { account: '', companies: 'companies', sites: 'sites', parameters: 'parameters', composition: 'composition' };
/** Adresses antérieures (en français, puis l'onglet commun sociétés et sites) : toujours acceptées. */
const TENANT_TAB_ALIASES: Readonly<Record<string, TenantTab>> = { societes: 'companies', organization: 'companies', parametres: 'parameters' };

/** Espace de travail Core de la boîte de travail, cible d'une navigation configurée. */
export const INBOX_WORKSPACE = 'core.workspace.inbox';
/** Espace de travail Core des paramètres du tenant (ADR-081) : l'onglet Paramètres de la page Tenant (ADR-128). */
export const PARAMETERS_WORKSPACE = 'core.workspace.tenant_parameters';
/** Espace de travail Core de la page Tenant (ADR-128). */
export const TENANT_WORKSPACE = 'core.workspace.tenant';
/** Espace de travail Core de la file d'attention (ADR-082 §9). */
export const ATTENTION_WORKSPACE = 'core.workspace.attention';
/** Espace de travail Core des thèmes du tenant (ADR-092 §3). */
export const THEMES_WORKSPACE = 'core.workspace.themes';
/** Espaces de travail Core de la médiathèque (ADR-106) : images et documents. */
export const MEDIA_IMAGES_WORKSPACE = 'core.workspace.media_images';
export const MEDIA_DOCUMENTS_WORKSPACE = 'core.workspace.media_documents';
/** Espace de travail Core de l'explorateur du Business Graph (ADR-129). */
export const GRAPH_WORKSPACE = 'core.workspace.graph';
/** Espace de travail Core de l'atlas du modèle (ADR-130). */
export const MODEL_WORKSPACE = 'core.workspace.model';
export const FORMS_WORKSPACE = 'core.workspace.forms';
export const VIEWS_WORKSPACE = 'core.workspace.views';

function workspaceRoute(ref: string): Route {
  if (ref === INBOX_WORKSPACE) return { kind: 'inbox' };
  if (ref === PARAMETERS_WORKSPACE) return { kind: 'tenant', tab: 'parameters' };
  if (ref === TENANT_WORKSPACE) return { kind: 'tenant', tab: 'account' };
  if (ref === ATTENTION_WORKSPACE) return { kind: 'attention' };
  if (ref === THEMES_WORKSPACE) return { kind: 'themes' };
  if (ref === MEDIA_IMAGES_WORKSPACE) return { kind: 'media', library: 'images' };
  if (ref === MEDIA_DOCUMENTS_WORKSPACE) return { kind: 'media', library: 'documents' };
  if (ref === GRAPH_WORKSPACE) return { kind: 'graph' };
  if (ref === MODEL_WORKSPACE) return { kind: 'model' };
  if (ref === FORMS_WORKSPACE) return { kind: 'forms' };
  if (ref === VIEWS_WORKSPACE) return { kind: 'views' };
  return ref === 'core.workspace.home' ? { kind: 'home' } : { kind: 'workspace', ref };
}

export type NavigationTarget = NonNullable<NavigationBody['items'][number]['target']>;

export function routeOfTarget(target: NavigationTarget): Route {
  switch (target.type) {
    case 'view':
      return { kind: 'view', view_ref: target.view_ref };
    case 'object': {
      const route: Route = { kind: 'object', object_type: target.object_type };
      if (target.record_ref !== undefined) route.record_id = target.record_ref;
      return route;
    }
    case 'workspace':
      return workspaceRoute(target.ref);
  }
}

const SEGMENT = /^[A-Za-z0-9_.-]+$/;

export function formatRoute(route: Route): string {
  switch (route.kind) {
    case 'home':
      return '#/';
    case 'view':
      return `#/view/${route.view_ref}${route.record_id === undefined ? '' : `/${route.record_id}`}${listSuffix(route.list)}`;
    case 'object':
      return `#/object/${route.object_type}${route.record_id === undefined ? '' : `/${route.record_id}`}${listSuffix(route.list)}`;
    case 'workspace':
      return `#/workspace/${route.ref}`;
    case 'inbox':
      return '#/inbox';
    case 'task':
      return `#/task/${route.work_item_id}`;
    case 'tenant':
      return TENANT_TAB_SEGMENTS[route.tab] === '' ? '#/tenant' : `#/tenant/${TENANT_TAB_SEGMENTS[route.tab]}`;
    case 'attention':
      return '#/attention';
    case 'themes':
      return '#/themes';
    case 'media':
      return `#/media/${route.library}${route.folder_id === undefined ? '' : `/${route.folder_id}`}`;
    case 'account':
      return '#/compte';
    case 'booking':
      return '#/rdv';
    case 'interview':
      return '#/entretien';
    case 'backoffice':
      return route.engagement_id === undefined ? '#/ayumia' : `#/ayumia/${route.engagement_id}`;
    case 'graph': {
      if (route.object_type === undefined || route.record_id === undefined) return '#/graph';
      const options = new URLSearchParams();
      if (route.mode === 'trace') options.set('mode', 'trace');
      if (route.depth !== undefined) options.set('depth', String(route.depth));
      if (route.mode === 'trace' && route.direction !== undefined) options.set('direction', route.direction);
      if (route.mode === 'trace' && route.at !== undefined) options.set('at', route.at);
      const query = options.toString();
      return `#/graph/${route.object_type}/${route.record_id}${query === '' ? '' : `?${query}`}`;
    }
    case 'model':
      return route.object_type === undefined ? '#/model' : `#/model/${route.object_type}`;
    case 'forms':
      return route.artifact_id === undefined ? '#/studio/forms' : `#/studio/forms/${route.artifact_id}`;
    case 'views':
      return route.artifact_id === undefined ? '#/studio/views' : `#/studio/views/${route.artifact_id}`;
  }
}

/** Réglages d'exploration lus dans l'adresse ; une valeur hors bornes est ignorée, jamais devinée. */
function graphOptions(query: string): GraphOptions {
  const params = new URLSearchParams(query);
  const options: GraphOptions = {};
  if (params.get('mode') === 'trace') options.mode = 'trace';
  const depth = Number(params.get('depth') ?? '');
  const maximum = options.mode === 'trace' ? 10 : 2;
  if (Number.isInteger(depth) && depth >= 1 && depth <= maximum) options.depth = depth;
  const direction = params.get('direction');
  if (options.mode === 'trace' && (direction === 'upstream' || direction === 'downstream')) options.direction = direction;
  const at = params.get('at');
  if (options.mode === 'trace' && at !== null && !Number.isNaN(Date.parse(at))) options.at = at;
  return options;
}

function listSuffix(list: string | undefined): string {
  return list === undefined || list === '' ? '' : `?${list}`;
}

/** Un fragment inconnu ou mal formé ramène à l'accueil : jamais une route devinée. */
export function parseRoute(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  const mark = raw.indexOf('?');
  const path = mark < 0 ? raw : raw.slice(0, mark);
  const list = mark < 0 ? '' : raw.slice(mark + 1);
  const route = parsePath(path);
  if ((route.kind === 'view' || route.kind === 'object') && list !== '') route.list = list;
  if (route.kind === 'graph' && route.record_id !== undefined && list !== '') return { ...route, ...graphOptions(list) };
  return route;
}

function parsePath(path: string): Route {
  const parts = path.split('/').filter((part) => part !== '');
  if (parts.some((part) => !SEGMENT.test(part))) return { kind: 'home' };
  const [kind, first, second] = parts;
  if (kind === 'view' && first !== undefined) return second === undefined ? { kind: 'view', view_ref: first } : { kind: 'view', view_ref: first, record_id: second };
  if (kind === 'object' && first !== undefined && /^[A-Z][A-Za-z0-9]*$/.test(first)) {
    return second === undefined ? { kind: 'object', object_type: first } : { kind: 'object', object_type: first, record_id: second };
  }
  if (kind === 'workspace' && first !== undefined) return workspaceRoute(first);
  if (kind === 'media' && (first === 'images' || first === 'documents')) {
    if (second === undefined) return { kind: 'media', library: first };
    if (/^[0-9a-f]{24}$/.test(second)) return { kind: 'media', library: first, folder_id: second };
  }
  if (kind === 'attention' && first === undefined) return { kind: 'attention' };
  // L'ancienne adresse des paramètres mène à l'onglet Paramètres de la page Tenant (ADR-128).
  if (kind === 'parameters' && first === undefined) return { kind: 'tenant', tab: 'parameters' };
  if (kind === 'tenant' && second === undefined) {
    const tab = (Object.keys(TENANT_TAB_SEGMENTS) as TenantTab[]).find((candidate) => TENANT_TAB_SEGMENTS[candidate] === (first ?? '')) ?? TENANT_TAB_ALIASES[first ?? ''];
    if (tab !== undefined) return { kind: 'tenant', tab };
  }
  if (kind === 'inbox' && first === undefined) return { kind: 'inbox' };
  if (kind === 'compte' && first === undefined) return { kind: 'account' };
  if (kind === 'themes' && first === undefined) return { kind: 'themes' };
  if (kind === 'rdv' && first === undefined) return { kind: 'booking' };
  if (kind === 'entretien' && first === undefined) return { kind: 'interview' };
  if (kind === 'ayumia' && first === undefined) return { kind: 'backoffice' };
  if (kind === 'ayumia' && first !== undefined && second === undefined && /^[0-9a-f]{24}$/.test(first)) return { kind: 'backoffice', engagement_id: first };
  if (kind === 'task' && first !== undefined && /^[0-9a-f]{24}$/.test(first)) return { kind: 'task', work_item_id: first };
  if (kind === 'graph' && first === undefined) return { kind: 'graph' };
  if (kind === 'graph' && first !== undefined && /^[A-Z][A-Za-z0-9]*$/.test(first) && second !== undefined && /^[0-9a-f]{24}$/.test(second) && parts.length === 3) return { kind: 'graph', object_type: first, record_id: second };
  if (kind === 'studio' && first === 'forms' && second === undefined && parts.length === 2) return { kind: 'forms' };
  if (kind === 'studio' && first === 'forms' && second !== undefined && /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/.test(second) && parts.length === 3) return { kind: 'forms', artifact_id: second };
  if (kind === 'studio' && first === 'views' && second === undefined && parts.length === 2) return { kind: 'views' };
  if (kind === 'studio' && first === 'views' && second !== undefined && /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_-]*)+$/.test(second) && parts.length === 3) return { kind: 'views', artifact_id: second };
  if (kind === 'model' && first === undefined) return { kind: 'model' };
  if (kind === 'model' && first !== undefined && second === undefined && /^[A-Z][A-Za-z0-9]*$/.test(first)) return { kind: 'model', object_type: first };
  return { kind: 'home' };
}

export function sameRoute(a: Route, b: Route): boolean {
  return formatRoute(a) === formatRoute(b);
}
